import { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiUpload, FiTrash2, FiImage, FiLoader, FiX, FiCheck, FiZoomIn } from 'react-icons/fi';
import Cropper from 'react-easy-crop';
import { companyAPI } from '../../services/api';
import { toast } from 'react-toastify';

const COLORS = {
  primary: '#059669',
  dark: '#064e3b',
  light: '#ecfdf5',
  accent: '#10b981',
  white: '#ffffff',
  textSecondary: '#6b7280',
  textMuted: '#9ca3af',
  border: 'rgba(16, 185, 129, 0.2)',
  danger: '#ef4444',
  dangerBg: 'rgba(239, 68, 68, 0.08)',
};

// ── Helper: crop image using Canvas API ──────────────────────────────────
function getCroppedImg(imageSrc, pixelCrop) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.crossOrigin = 'anonymous';
    image.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = pixelCrop.width;
      canvas.height = pixelCrop.height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(
        image,
        pixelCrop.x, pixelCrop.y,
        pixelCrop.width, pixelCrop.height,
        0, 0,
        pixelCrop.width, pixelCrop.height,
      );
      canvas.toBlob((blob) => {
        if (!blob) { reject(new Error('Canvas is empty')); return; }
        resolve(blob);
      }, 'image/png');
    };
    image.onerror = reject;
    image.src = imageSrc;
  });
}

export default function CompanySignatures({ companyId, isExpanded }) {
  const [signatures, setSignatures] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [label, setLabel] = useState('');
  const [thumbnails, setThumbnails] = useState({});
  const fileInputRef = useRef(null);
  const thumbnailsRef = useRef({});

  // Crop modal state
  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [imageSrc, setImageSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

  const fetchSignatures = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const res = await companyAPI.getSignatures(companyId);
      const data = Array.isArray(res.data) ? res.data : [];
      setSignatures(data);

      // Fetch thumbnails
      data.forEach((sig) => {
        if (!thumbnailsRef.current[sig.id]) {
          companyAPI.downloadSignature(sig.id).then((res) => {
            const url = URL.createObjectURL(res.data);
            thumbnailsRef.current[sig.id] = url;
            setThumbnails((prev) => ({ ...prev, [sig.id]: url }));
          }).catch(() => {});
        }
      });
    } catch {
      toast.error('Failed to load signatures');
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    if (isExpanded) fetchSignatures();
  }, [isExpanded, fetchSignatures]);

  // Cleanup object URLs on unmount
  useEffect(() => {
    return () => {
      Object.values(thumbnailsRef.current).forEach((url) => URL.revokeObjectURL(url));
    };
  }, []);

  // ── File select → open crop modal ──────────────────────────────────────
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    e.target.value = '';
    if (!file) return;

    if (!label.trim()) {
      toast.error('Please enter a name for the signature');
      return;
    }
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be under 5MB');
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      setImageSrc(reader.result);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setCroppedAreaPixels(null);
      setCropModalOpen(true);
    };
    reader.readAsDataURL(file);
  };

  const onCropComplete = useCallback((_croppedArea, croppedPixels) => {
    setCroppedAreaPixels(croppedPixels);
  }, []);

  // ── Crop confirm → upload cropped image ────────────────────────────────
  const handleCropConfirm = async () => {
    if (!imageSrc || !croppedAreaPixels) return;

    setUploading(true);
    try {
      const croppedBlob = await getCroppedImg(imageSrc, croppedAreaPixels);
      const formData = new FormData();
      formData.append('file', croppedBlob, 'signature.png');
      formData.append('label', label.trim());

      await companyAPI.uploadSignature(companyId, formData);
      toast.success('Signature uploaded successfully');
      setLabel('');
      setCropModalOpen(false);
      setImageSrc(null);
      await fetchSignatures();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const closeCropModal = () => {
    setCropModalOpen(false);
    setImageSrc(null);
    setCroppedAreaPixels(null);
  };

  const handleDelete = async (sigId) => {
    if (!window.confirm('Delete this signature?')) return;
    try {
      await companyAPI.deleteSignature(sigId);
      if (thumbnailsRef.current[sigId]) {
        URL.revokeObjectURL(thumbnailsRef.current[sigId]);
        delete thumbnailsRef.current[sigId];
      }
      setThumbnails((prev) => {
        const next = { ...prev };
        delete next[sigId];
        return next;
      });
      toast.success('Signature deleted');
      await fetchSignatures();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Delete failed');
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  if (!isExpanded) return null;

  return (
    <motion.div
      initial={{ height: 0, opacity: 0 }}
      animate={{ height: 'auto', opacity: 1 }}
      exit={{ height: 0, opacity: 0 }}
      transition={{ duration: 0.3 }}
      style={{ overflow: 'hidden' }}
    >
      <div style={{ paddingTop: 8 }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: 16 }}>
            <FiLoader size={20} style={{ animation: 'spin 1s linear infinite', color: COLORS.primary }} />
          </div>
        ) : signatures.length === 0 ? (
          <p style={{ fontSize: 13, color: COLORS.textMuted, textAlign: 'center', padding: '12px 0' }}>
            No signatures uploaded yet
          </p>
        ) : (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))',
            gap: 10,
            marginBottom: 8,
          }}>
            <AnimatePresence>
              {signatures.map((sig) => (
                <motion.div
                  key={sig.id}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  style={{
                    position: 'relative',
                    background: COLORS.white,
                    border: `1px solid ${COLORS.border}`,
                    borderRadius: 10,
                    padding: 8,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 6,
                  }}
                >
                  {/* Thumbnail */}
                  <div style={{
                    width: '100%',
                    height: 70,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: COLORS.light,
                    borderRadius: 6,
                    overflow: 'hidden',
                  }}>
                    {thumbnails[sig.id] ? (
                      <img
                        src={thumbnails[sig.id]}
                        alt={sig.label || sig.file_name}
                        style={{ maxWidth: '100%', maxHeight: 70, objectFit: 'contain' }}
                      />
                    ) : (
                      <FiImage size={24} color={COLORS.textMuted} />
                    )}
                  </div>

                  {/* Label / filename */}
                  <div style={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: COLORS.dark,
                    textAlign: 'center',
                    width: '100%',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}>
                    {sig.label || sig.file_name}
                  </div>

                  {/* Size */}
                  <div style={{ fontSize: 10, color: COLORS.textMuted }}>
                    {formatFileSize(sig.file_size)}
                  </div>

                  {/* Delete button */}
                  <motion.button
                    whileHover={{ scale: 1.15 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={() => handleDelete(sig.id)}
                    style={{
                      position: 'absolute',
                      top: 4,
                      right: 4,
                      width: 22,
                      height: 22,
                      borderRadius: '50%',
                      border: 'none',
                      background: COLORS.dangerBg,
                      color: COLORS.danger,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: 0,
                    }}
                  >
                    <FiTrash2 size={11} />
                  </motion.button>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        )}

        {/* Upload area */}
        <div style={{
          display: 'flex',
          gap: 8,
          alignItems: 'center',
          flexWrap: 'wrap',
          marginTop: 4,
        }}>
          <input
            type="text"
            placeholder="Signature name *"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            style={{
              flex: 1,
              minWidth: 100,
              padding: '8px 12px',
              border: `1px solid ${COLORS.border}`,
              borderRadius: 8,
              fontSize: 13,
              outline: 'none',
              background: COLORS.white,
            }}
            onFocus={(e) => {
              e.target.style.borderColor = COLORS.accent;
              e.target.style.boxShadow = '0 0 0 3px rgba(16,185,129,0.15)';
            }}
            onBlur={(e) => {
              e.target.style.borderColor = COLORS.border;
              e.target.style.boxShadow = 'none';
            }}
          />
          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg,image/gif,image/webp"
            onChange={handleFileSelect}
            style={{ display: 'none' }}
            disabled={uploading}
          />
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              padding: '8px 16px',
              background: COLORS.light,
              border: `1px dashed ${COLORS.border}`,
              borderRadius: 8,
              color: COLORS.primary,
              fontSize: 13,
              fontWeight: 600,
              cursor: uploading ? 'not-allowed' : 'pointer',
              opacity: uploading ? 0.6 : 1,
              whiteSpace: 'nowrap',
            }}
          >
            {uploading ? (
              <>
                <FiLoader size={14} style={{ animation: 'spin 1s linear infinite' }} />
                Uploading...
              </>
            ) : (
              <>
                <FiUpload size={14} />
                Upload Signature
              </>
            )}
          </motion.button>
        </div>
      </div>

      {/* ── Crop Modal ──────────────────────────────────────────────────── */}
      <AnimatePresence>
        {cropModalOpen && imageSrc && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: 9999,
              background: 'rgba(6, 78, 59, 0.5)',
              backdropFilter: 'blur(6px)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 20,
            }}
            onClick={closeCropModal}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{ duration: 0.25 }}
              style={{
                background: '#fff',
                borderRadius: 20,
                width: '100%',
                maxWidth: 520,
                boxShadow: '0 25px 60px rgba(0,0,0,0.25)',
                overflow: 'hidden',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '20px 24px 16px',
                borderBottom: '1px solid rgba(16,185,129,0.12)',
              }}>
                <h3 style={{
                  fontFamily: 'Poppins, sans-serif',
                  fontSize: 18,
                  fontWeight: 700,
                  color: '#1f2937',
                  margin: 0,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                }}>
                  <div style={{
                    width: 36, height: 36, borderRadius: 10,
                    background: 'rgba(16,185,129,0.1)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <FiImage size={18} color={COLORS.primary} />
                  </div>
                  Crop Signature
                </h3>
                <button
                  onClick={closeCropModal}
                  style={{
                    width: 32, height: 32, borderRadius: 10,
                    border: 'none', background: 'rgba(0,0,0,0.05)',
                    cursor: 'pointer', display: 'flex',
                    alignItems: 'center', justifyContent: 'center',
                  }}
                >
                  <FiX size={16} color="#6b7280" />
                </button>
              </div>

              {/* Cropper area */}
              <div style={{
                position: 'relative',
                width: '100%',
                height: 320,
                background: '#f3f4f6',
              }}>
                <Cropper
                  image={imageSrc}
                  crop={crop}
                  zoom={zoom}
                  aspect={undefined}
                  onCropChange={setCrop}
                  onZoomChange={setZoom}
                  onCropComplete={onCropComplete}
                  style={{
                    containerStyle: { borderRadius: 0 },
                  }}
                />
              </div>

              {/* Zoom control */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: '14px 24px',
                borderTop: '1px solid rgba(16,185,129,0.12)',
              }}>
                <FiZoomIn size={16} color={COLORS.textSecondary} />
                <input
                  type="range"
                  min={1}
                  max={3}
                  step={0.05}
                  value={zoom}
                  onChange={(e) => setZoom(Number(e.target.value))}
                  style={{
                    flex: 1,
                    height: 4,
                    accentColor: COLORS.primary,
                    cursor: 'pointer',
                  }}
                />
                <span style={{ fontSize: 12, color: COLORS.textSecondary, minWidth: 36, textAlign: 'right' }}>
                  {Math.round(zoom * 100)}%
                </span>
              </div>

              {/* Actions */}
              <div style={{
                display: 'flex',
                gap: 12,
                justifyContent: 'flex-end',
                padding: '16px 24px 20px',
                borderTop: '1px solid rgba(16,185,129,0.12)',
              }}>
                <button
                  onClick={closeCropModal}
                  style={{
                    padding: '10px 20px',
                    borderRadius: 10,
                    border: '1px solid #d1d5db',
                    background: '#fff',
                    color: '#374151',
                    fontSize: 14,
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  Cancel
                </button>
                <motion.button
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                  onClick={handleCropConfirm}
                  disabled={uploading}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '10px 24px',
                    borderRadius: 10,
                    border: 'none',
                    background: 'linear-gradient(135deg, #059669, #10b981)',
                    color: '#fff',
                    fontSize: 14,
                    fontWeight: 600,
                    cursor: uploading ? 'not-allowed' : 'pointer',
                    opacity: uploading ? 0.7 : 1,
                    boxShadow: '0 4px 15px rgba(16, 185, 129, 0.3)',
                  }}
                >
                  {uploading ? (
                    <>
                      <FiLoader size={15} style={{ animation: 'spin 1s linear infinite' }} />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <FiCheck size={15} />
                      Crop & Upload
                    </>
                  )}
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
